/**
 * Historie API — the shared change-history reader (`/v1/historie/*`, design:
 * .agents/historie-zmen-plan.md). Admin-only per module; the tyre-set detail
 * and the servis event modal read their own record-level endpoints (open to
 * every module reader) but get the same entry shape.
 */

import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { components } from '@/types/api';

export type HistorieModul = components['schemas']['HistorieModul'];
export type HistorieZaznam = components['schemas']['HistorieZaznam'];
export type HistorieZmena = components['schemas']['HistorieZmena'];
type HistorieResponse = components['schemas']['HistorieResponse'];
export type HistorieMeta = components['schemas']['HistorieMetaResponse'];

export interface HistorieFilters {
  entita?: string;
  id_zaznamu?: number;
  id_uzivatele?: number;
  od?: string;
  do?: string;
}

function buildQuery(filters: HistorieFilters & { kurzor?: string; limit?: number }): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') qs.set(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export function useHistorieInfiniteQuery(
  modul: HistorieModul,
  filters: HistorieFilters,
  options: { enabled?: boolean; pageSize?: number } = {},
) {
  const pageSize = options.pageSize ?? 50;
  return useInfiniteQuery({
    queryKey: ['historie', modul, filters, pageSize],
    enabled: options.enabled ?? true,
    queryFn: ({ pageParam, signal }) =>
      api.get<HistorieResponse>(
        `/v1/historie/${modul}${buildQuery({ ...filters, kurzor: pageParam, limit: pageSize })}`,
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.dalsi_kurzor ?? undefined,
    placeholderData: (previous) => previous,
  });
}

function metaQuery(modul: HistorieModul) {
  return {
    queryKey: ['historie', modul, 'meta'],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.get<HistorieMeta>(`/v1/historie/${modul}/meta`, { signal }),
  };
}

/** Filter options (submodules, authors) of each covered module. */
export function useHistorieMetaQueries(moduly: HistorieModul[]) {
  return useQueries({ queries: moduly.map(metaQuery) });
}

/** `2026-04-14 12:34:56` → `14. 4. 2026 12:34`. */
export function historieKdy(s: string): string {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return s;
  return `${Number(m[3])}. ${Number(m[2])}. ${m[1]} ${m[4]}:${m[5]}`;
}

/**
 * One-line summary of a change: "Sezóna: Letní → Zimní",
 * "Tažné zařízení · Cena: 15 000 Kč → 16 000 Kč", "Přidáno · Brno", …
 */
export function historieZmenaText(z: HistorieZmena): string {
  const label = z.pole_label ?? z.polozka ?? z.sekce_label;
  if (z.pole && z.polozka) return `${z.polozka} · ${label}: ${z.puvodni ?? '—'} → ${z.nova ?? '—'}`;
  if (z.pole) return `${label}: ${z.puvodni ?? '—'} → ${z.nova ?? '—'}`;
  if (z.polozka) return `${z.akce_label} · ${z.polozka}`;
  return z.akce_label;
}

export interface HistorieRadek {
  key: number;
  date: string;
  title: string;
  sub: string;
}

/**
 * Timeline rows of one record's history (tyre-set detail, servis event
 * modal): one per change, newest save first; the date column carries the
 * day, the sub line the time and author. Labels and values arrive formatted
 * from the shared historie reader.
 */
export function historieRadky(zaznamy: HistorieZaznam[]): HistorieRadek[] {
  return zaznamy.flatMap((z) =>
    z.zmeny.map((zm) => {
      const author = z.autor?.jmeno ?? '';
      const zmena = zm.pole ? `${zm.puvodni ?? '—'} → ${zm.nova ?? '—'}` : '';
      const d = z.vytvoreno_at.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
      const cas = d?.[4] ? `${d[4]}:${d[5]}` : '';
      return {
        key: zm.id_zmeny,
        date: d ? `${d[3]}.${d[2]}.${d[1].slice(-2)}` : z.vytvoreno_at,
        title: zm.pole_label ? `${zm.akce_label} · ${zm.pole_label}` : zm.akce_label,
        sub: [zmena, zm.poznamka, cas, author].filter(Boolean).join(' · '),
      };
    }),
  );
}

/** Latest entries of a module — the compact feeds (Správa firmy → Přehled). */
export function useHistorieLatestQuery(modul: HistorieModul, limit: number, enabled = true) {
  return useQuery({
    queryKey: ['historie', modul, 'latest', limit],
    enabled,
    queryFn: ({ signal }) =>
      api.get<HistorieResponse>(`/v1/historie/${modul}${buildQuery({ limit })}`, { signal }),
  });
}
